import {
  FUTURES_ORDER_TREND_LABELS,
  FUTURES_ORDER_TREND_OPTIONS,
} from './futuresOrderConstants';
import {
  FUTURES_ROUND_STATE_OPTIONS,
  inputDateTimeToQueryApi,
} from './futuresRoundConstants';

export const FOLLOW_VISIBILITY_LABELS = {
  ALL: '所有用戶',
  AGENT_TEAM: '代理團隊',
  MEMBER_IDS: '特定會員',
};

/** 跟單新增：顯示類型（暫不含 USER_LEVEL） */
export const FOLLOW_VISIBILITY_CREATE_OPTIONS = [
  { value: 'ALL', label: '所有用戶' },
  { value: 'AGENT_TEAM', label: '代理團隊' },
  { value: 'MEMBER_IDS', label: '特定會員' },
];

/** 跟單新增：金額類型 */
export const FOLLOW_AMOUNT_TYPE_OPTIONS = [
  { value: 'REMAINING_PERCENTAGE', label: '餘額百分比' },
  { value: 'MANUAL_AMOUNT', label: '手動輸入金額' },
];

export const FOLLOW_AMOUNT_TYPE_LABELS = {
  REMAINING_PERCENTAGE: '餘額百分比',
  MANUAL_AMOUNT: '手動輸入金額',
};

/** 跟單新增：投注類型（不含持平） */
export const FOLLOW_ORDER_TREND_CREATE_OPTIONS = [
  { value: 'UP', label: '做多' },
  { value: 'DOWN', label: '做空' },
];

export function defaultFollowCreateForm() {
  return {
    name: '',
    parentProductId: '',
    periodProductId: '',
    roundId: '',
    signals: '',
    orderTrend: 'UP',
    amountType: 'REMAINING_PERCENTAGE',
    amountPercent: '',
    amountMin: '',
    amountMax: '',
    followFeeRate: '',
    visibilityType: 'ALL',
    teamIds: '',
  };
}

export function buildFollowCreatePayload(form) {
  const payload = {
    name: form.name.trim(),
    roundId: Number(form.roundId),
    orderTrend: form.orderTrend,
    visibilityType: form.visibilityType,
    followFeeRate: Number(form.followFeeRate),
    amountType: form.amountType,
    amountValue: '',
    usingState: 1,
  };
  const signals = form.signals?.trim();
  if (signals) {
    payload.signals = signals;
  }
  if (form.amountType === 'REMAINING_PERCENTAGE') {
    payload.amountValue = String(form.amountPercent).trim();
  } else {
    payload.amountValue = `${String(form.amountMin).trim()},${String(form.amountMax).trim()}`;
  }
  if (form.visibilityType === 'AGENT_TEAM') {
    payload.topInviterIds = form.teamIds
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .join(',');
  }
  return payload;
}

export function validateFollowCreateForm(form) {
  const name = form.name?.trim();
  if (!name) {
    return '請輸入標題';
  }
  if (name.length > 20) {
    return '標題最多 20 個字元';
  }
  if (!form.parentProductId) {
    return '請選擇交易對';
  }
  if (!form.periodProductId) {
    return '請選擇週期';
  }
  if (!form.roundId) {
    return '請選擇時間';
  }
  const signals = form.signals?.trim();
  if (signals && signals.length > 20) {
    return '自訂口令最多 20 個字元';
  }
  if (!form.orderTrend) {
    return '請選擇投注類型';
  }
  if (!form.amountType) {
    return '請選擇金額類型';
  }
  if (form.amountType === 'REMAINING_PERCENTAGE') {
    const percent = Number(form.amountPercent);
    if (form.amountPercent === '' || Number.isNaN(percent) || percent <= 0) {
      return '請輸入有效的餘額百分比';
    }
  } else {
    const min = Number(form.amountMin);
    const max = Number(form.amountMax);
    if (form.amountMin === '' || form.amountMax === '' || Number.isNaN(min) || Number.isNaN(max)) {
      return '請輸入最小與最大金額';
    }
    if (min > max) {
      return '最小金額不可大於最大金額';
    }
    if (min <= 0 || max <= 0) {
      return '金額必須大於 0';
    }
  }
  const feeRate = Number(form.followFeeRate);
  if (form.followFeeRate === '' || Number.isNaN(feeRate) || feeRate < 0) {
    return '請輸入有效的跟單費用（%）';
  }
  if (!form.visibilityType) {
    return '請選擇顯示類型';
  }
  if (form.visibilityType === 'AGENT_TEAM') {
    const raw = form.teamIds?.trim();
    if (!raw) {
      return '請輸入團隊 ID';
    }
    const ids = raw.split(',').map((item) => item.trim()).filter(Boolean);
    if (ids.length === 0) {
      return '請輸入有效的團隊 ID';
    }
    if (ids.some((id) => !/^\d+$/.test(id))) {
      return '團隊 ID 須為數字';
    }
  }
  return '';
}

export function formatRoundOptionLabel(round) {
  if (!round) {
    return '';
  }
  const issue = round.issueNumber || round.roundCode || '';
  const start = round.startTime ? round.startTime.replace('T', ' ').slice(0, 19) : '';
  const end = round.endTime ? round.endTime.replace('T', ' ').slice(0, 19) : '';
  if (start && end) {
    return `${issue}（${start} ~ ${end}）`;
  }
  return issue || String(round.id);
}

export function formatFuturesProductLabel(product) {
  if (!product) {
    return '';
  }
  const name = product.displayName || product.name || product.marketSymbolCode || '';
  const symbol = product.marketSymbolCode;
  if (symbol && name !== symbol) {
    return `${name}（${symbol}）`;
  }
  return name || symbol || String(product.id);
}

export function isProductOrderable(product) {
  return Number(product?.status) === 1 && Number(product?.exchangeStatus) === 1;
}

export const FOLLOW_STATUS_FILTER_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'OPEN', label: '進行中' },
  { value: 'CLOSED', label: '已結束' },
  { value: 'DISABLED', label: '停用' },
];

const EMPTY_FILTERS = {
  roundCode: '',
  orderTrend: '',
  state: '',
  createTimeFromInput: '',
  createTimeToInput: '',
};

export function defaultFollowFilters() {
  return { ...EMPTY_FILTERS };
}

export function formatAmount(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

export { formatSignedProfitLoss as formatProfitLoss } from '../../utils/orderDisplayFormat';

export function canDisableFollowOrder(row) {
  return row?.statusLabel !== '已結束' && row?.usingState !== 0;
}

export function buildFollowPageParams(pageNo, filters) {
  const params = { pageNo, pageSize: 15 };
  const roundCode = filters.roundCode?.trim();
  if (roundCode) {
    params.roundCode = roundCode;
  }
  if (filters.orderTrend) {
    params.orderTrend = filters.orderTrend;
  }
  if (filters.state === 'DISABLED') {
    params.usingState = 0;
  } else if (filters.state) {
    params.state = filters.state;
  }
  const createTimeFrom = inputDateTimeToQueryApi(filters.createTimeFromInput);
  const createTimeTo = inputDateTimeToQueryApi(filters.createTimeToInput);
  if (createTimeFrom) {
    params.createTimeFrom = createTimeFrom.replace('T', ' ');
  }
  if (createTimeTo) {
    params.createTimeTo = createTimeTo.replace('T', ' ');
  }
  return params;
}

export {
  FUTURES_ORDER_TREND_OPTIONS,
  FUTURES_ORDER_TREND_LABELS,
};
